import {
  AuditLogEntryFieldsFragment_,
  BusinessUnitAssessment_,
  CompanyAssessment,
  CompanyAssessmentContextQuery_,
  EsrsAssessmentContextQuery_,
  EsrsAssessmentQuery_,
  QuestionType_Enum_,
  ShortUser,
  useAuditLogEntriesQuery,
  useBusinessUnitAssessmentLazyQuery_,
  useBusinessUnitAssessmentQuery,
  useCompanyAssessmentContextQuery,
  useEsrsAssessmentContextQuery,
  useEsrsAuditLogEntriesQuery,
} from 'models';
import { useCurrentCompanyId } from 'utils/hooks';
import { auditLogsTablesMap } from './auditLogsTablesMap';
import { taxonomyAuditLogsCases } from './taxonomyAuditLogsCases';
import { generalAuditLogsCases } from './generalAuditLogsCases';
import { useEffect, useState } from 'react';
import { esrsAuditLogsCases } from './esrsAuditLogsCases';

export const COMPANY_LEVEL = 'Company level';
export const TARGET_INITIAL_COUNT = 50;
export const TARGET_LOAD_MORE_COUNT = 20;
export const FETCH_BATCH_SIZE = 50;

export type DataChanges =
  | {
      oldData?: string;
      newData?: string;
      oldFileExtension?: string;
      newFileExtension?: string;
      questionType?: QuestionType_Enum_;
    }[]
  | null;

export type AuditLog = {
  id: string;
  user?: ShortUser;
  operation: string;
  table: string;
  contextualInfo: string;
  metaInfo: { [key: string]: string };
  dataChanges: DataChanges;
  withReplacement?: boolean;
  isSkipped?: boolean;
};

export type ActivityReport = {
  activityRef: string;
  id: any;
  activity: {
    name: string;
  };
};

export type TableSpecificDataType = {
  contextualInfo: string;
  metaInfo: { [key: string]: string };
  dataChanges: DataChanges;
  operation: string;
  withReplacement?: boolean;
  isSkipped?: boolean;
};

export type TaxonomyAssessmentContext = Omit<CompanyAssessmentContextQuery_, 'company'> & {
  company?: Omit<NonNullable<CompanyAssessmentContextQuery_['company']>, 'bAssesssments'> & {
    bAssesssments: BusinessUnitAssessment_[];
  };
};

const extractContext = (data?: TaxonomyAssessmentContext['company'] | null): string[] => {
  const context: string[] = [];

  const traverse = (obj: any) => {
    for (const key in obj) {
      if (key === 'id') {
        context.push(obj[key]);
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        traverse(obj[key]);
      }
    }
  };

  traverse(data);

  return context;
};

const getTableSpecificData = (
  auditLog: AuditLogEntryFieldsFragment_,
  contextData: TaxonomyAssessmentContext
): TableSpecificDataType => {
  let tableSpecificData: TableSpecificDataType = {
    contextualInfo: '',
    metaInfo: {},
    dataChanges: [],
    operation: '',
    withReplacement: false,
    isSkipped: false,
  };

  const { table, schema } = auditLog;

  if (schema === 'public' && auditLogsTablesMap.taxonomy.includes(table)) {
    tableSpecificData = taxonomyAuditLogsCases({
      contextData,
      auditLog,
    });
  } else if (schema === 'public' && auditLogsTablesMap.general.includes(table)) {
    tableSpecificData = generalAuditLogsCases({
      contextData,
      auditLog,
    });
  }
  return tableSpecificData;
};

const getEsrsTableSpecificData = (
  auditLog: AuditLogEntryFieldsFragment_,
  contextData: EsrsAssessmentContextQuery_['EsrsAssessment_by_pk'],
  isMetric?: boolean
): TableSpecificDataType => {
  let tableSpecificData: TableSpecificDataType = {
    contextualInfo: '',
    metaInfo: {},
    dataChanges: [],
    operation: '',
    withReplacement: false,
    isSkipped: false,
  };

  tableSpecificData = esrsAuditLogsCases({
    contextData,
    auditLog,
    isMetric,
  });
  return tableSpecificData;
};

const mapAuditLogs = (
  data: AuditLogEntryFieldsFragment_[],
  context: TaxonomyAssessmentContext
): AuditLog[] => {
  return data?.map((auditLog) => {
    const { contextualInfo, metaInfo, dataChanges, operation, withReplacement, isSkipped } =
      getTableSpecificData(auditLog, context);
    return {
      id: auditLog?.invocationId,
      user: auditLog?.user ?? undefined,
      operation: operation,
      table: auditLog?.table,
      contextualInfo,
      metaInfo,
      dataChanges,
      withReplacement: withReplacement,
      isSkipped: isSkipped,
    };
  });
};

const mapEsrsAuditLogs = (
  data: AuditLogEntryFieldsFragment_[],
  context: EsrsAssessmentContextQuery_['EsrsAssessment_by_pk'],
  isMetric?: boolean
): AuditLog[] => {
  return data?.map((auditLog) => {
    const { contextualInfo, metaInfo, dataChanges, operation, withReplacement, isSkipped } =
      getEsrsTableSpecificData(auditLog, context, isMetric);
    return {
      id: auditLog?.invocationId,
      user: auditLog?.user ?? undefined,
      operation: operation,
      table: auditLog?.table,
      contextualInfo,
      metaInfo,
      dataChanges,
      withReplacement: withReplacement,
      isSkipped: isSkipped,
    };
  });
};

export const useTaxonomyContext = (cAssessment: CompanyAssessment) => {
  const [contextData, setContextData] = useState<TaxonomyAssessmentContext>();
  const [loading, setLoading] = useState(false);

  const {
    data: cAssessmentContextData,
    loading: cAssessmentContextLoading,
    refetch: refetchCAssessmentContext,
  } = useCompanyAssessmentContextQuery({
    variables: { cAssessmentId: cAssessment?.id },
    skip: !cAssessment,
  });

  const [loadBusinessUnitAssessment] = useBusinessUnitAssessmentLazyQuery_();

  const refetch = async () => {
    await refetchCAssessmentContext();
  };

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      if (!cAssessmentContextData?.company?.bAssesssments) return;

      setLoading(true);
      try {
        const bAssessmentIds = cAssessmentContextData.company.bAssesssments.map((b) => b.id) || [];

        const results = await Promise.all(
          bAssessmentIds.map((id) => loadBusinessUnitAssessment({ variables: { id } }))
        );

        const bAssessments = results
          .map((r) => r.data?.bAssessment)
          .filter(Boolean) as BusinessUnitAssessment_[];

        setContextData({
          ...cAssessmentContextData,
          company: {
            ...cAssessmentContextData.company,
            bAssesssments: bAssessments,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    if (!cAssessmentContextLoading && cAssessmentContextData) {
      fetchBusinessUnits();
    }
  }, [cAssessmentContextData, cAssessmentContextLoading, loadBusinessUnitAssessment]);

  return {
    contextData,
    loading: loading || cAssessmentContextLoading,
    refetch,
  };
};

export const useTaxonomyAuditLogs = ({
  cAssessment,
  limit = TARGET_INITIAL_COUNT,
  offset = 0,
  skip = false,
}: {
  cAssessment: CompanyAssessment;
  limit?: number;
  offset?: number;
  skip?: boolean;
}) => {
  const { companyId } = useCurrentCompanyId();
  const [refetching, setRefetching] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);

  const {
    contextData,
    loading: contextLoading,
    refetch: refetchContext,
  } = useTaxonomyContext(cAssessment);

  const context = extractContext(contextData?.company);

  const {
    data,
    loading,
    refetch: refetchAuditLogs,
  } = useAuditLogEntriesQuery({
    variables: {
      companyId,
      limit,
      offset,
    },
    skip: !companyId || !limit || skip || !contextData || contextLoading,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.auditLogEntries) {
      setHasMoreData(data.auditLogEntries.length >= limit);
    }
  }, [data?.auditLogEntries, limit]);

  const refetch = async () => {
    setRefetching(true);
    await refetchContext();
    await refetchAuditLogs({ limit: TARGET_INITIAL_COUNT, offset: 0 });
    setRefetching(false);
  };

  const dataForCAssessment = data?.auditLogEntries.filter((auditLog) => {
    if (auditLog.table === 'ReportDownloads') return context.includes(auditLog.companyId);
    return (
      context.includes(auditLog?.newData?.id) ||
      (auditLog.table === 'PortfolioCompany' &&
        (context.includes(auditLog?.newData?.sharedCompanyAssessmentId) ||
          context.includes(auditLog?.oldData?.sharedCompanyAssessmentId)))
    );
  });

  const auditLogs =
    dataForCAssessment && contextData ? mapAuditLogs(dataForCAssessment, contextData) : [];

  return {
    auditLogs,
    loading: loading || contextLoading,
    refetch,
    hasMoreData,
    refetchLoading: refetching,
  };
};

export const useEsrsAuditLogs = ({
  esrsAssessment,
  answerId,
  limit = TARGET_INITIAL_COUNT,
  offset = 0,
  skip = false,
  isSidebar,
}: {
  esrsAssessment?: EsrsAssessmentQuery_['esrsAssessment'];
  answerId?: string;
  limit?: number;
  offset?: number;
  skip?: boolean;
  isSidebar?: boolean;
}) => {
  const { companyId } = useCurrentCompanyId();
  const [refetching, setRefetching] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);

  const {
    data: contextData,
    loading: contextLoading,
    refetch: refetchContext,
  } = useEsrsAssessmentContextQuery({
    variables: {
      esrsAssessmentId: esrsAssessment?.id,
    },
    skip: !esrsAssessment || !limit || skip,
  });

  const {
    data,
    loading,
    refetch: refetchAuditLogs,
  } = useEsrsAuditLogEntriesQuery({
    fetchPolicy: 'network-only',
    variables: {
      companyId,
      limit,
      offset,
    },
    skip: !companyId,
  });

  const filteredAuditLogs = data?.auditLogEntries.filter(
    (log) =>
      !(log.schema === 'esrs' && log.table === 'Answer' && log.oldData === null) &&
      !(
        log.schema === 'esrs' &&
        log.table === 'Answer' &&
        log.oldData?.hasOptedOut === log.newData?.hasOptedOut
      ) &&
      (isSidebar ? log.table === 'Answer' || log.newData.answerId === answerId : true)
  );

  useEffect(() => {
    if (data?.auditLogEntries) {
      setHasMoreData(data.auditLogEntries.length >= limit);
    }
  }, [data?.auditLogEntries, limit]);

  const refetch = async () => {
    setRefetching(true);
    await refetchContext();
    await refetchAuditLogs();
    setRefetching(false);
  };

  const auditLogs =
    contextData && filteredAuditLogs
      ? mapEsrsAuditLogs(filteredAuditLogs, contextData.EsrsAssessment_by_pk, !!answerId)
      : [];

  return {
    auditLogs,
    context: contextData?.EsrsAssessment_by_pk,
    loading: loading || contextLoading,
    refetch,
    hasMoreData,
    refetchLoading: refetching,
  };
};
